import React, { useState, useEffect } from 'react';
import { useNotification } from '../hooks/useNotification';
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Shield, 
  User, 
  Key,
  Save,
  X
} from 'lucide-react';

const Admin = () => {
  const { showSuccess, showError, showWarning } = useNotification();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({ username: '', password: '' });
  const [editUser, setEditUser] = useState({ role: '', password: '' });

  useEffect(() => {
    // Check if user is admin
    const userRole = localStorage.getItem('userRole');
    if (userRole !== 'admin') {
      showError('Доступ запрещён. Требуются права администратора.');
      return;
    }
    
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      
      if (!token) {
        showError('Токен авторизации не найден. Войдите снова.');
        return;
      }
      
      console.log('Запрос пользователей из:', `${apiUrl}/api/admin/users`);
      console.log('Токен существует:', !!token);
      
      const response = await fetch(`${apiUrl}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Статус ответа:', response.status);
      console.log('Заголовки ответа:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Ошибка ответа:', errorText);
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: `HTTP ${response.status}: ${errorText}` };
        }
        
        throw new Error(errorData.message || 'Не удалось получить пользователей');
      }

      const data = await response.json();
      console.log('Данные пользователей:', data);
      setUsers(data.users || []);
    } catch (err) {
      console.error('Ошибка получения пользователей:', err);
      showError(err.message || 'Не удалось получить пользователей');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.username.trim() || !newUser.password.trim()) {
      showError('Имя пользователя и пароль обязательны');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const response = await fetch(`${apiUrl}/api/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newUser)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Не удалось создать пользователя' }));
        throw new Error(error.message || 'Не удалось создать пользователя');
      }

      showSuccess('Пользователь успешно создан');
      setNewUser({ username: '', password: '' });
      setShowCreateModal(false);
      fetchUsers();
    } catch (err) {
      showError(err.message || 'Не удалось создать пользователя');
    }
  };

  const handleEditUser = async () => {
    if (!editingUser) return;

    const updateData = {};
    if (editUser.role) updateData.role = editUser.role;
    if (editUser.password.trim()) updateData.password = editUser.password;

    if (Object.keys(updateData).length === 0) {
      showError('Укажите роль или пароль для обновления');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const response = await fetch(`${apiUrl}/api/admin/users/${editingUser._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Не удалось обновить пользователя' }));
        throw new Error(error.message || 'Не удалось обновить пользователя');
      }

      showSuccess('Пользователь успешно обновлён');
      setEditUser({ role: '', password: '' });
      setShowEditModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      showError(err.message || 'Не удалось обновить пользователя');
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Вы уверены, что хотите удалить пользователя "${user.username}"? Это действие необратимо.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const response = await fetch(`${apiUrl}/api/admin/users/${user._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Не удалось удалить пользователя' }));
        throw new Error(error.message || 'Не удалось удалить пользователя');
      }

      showSuccess('Пользователь успешно удалён');
      fetchUsers();
    } catch (err) {
      showError(err.message || 'Не удалось удалить пользователя');
    }
  };

  const handleRevokeSessions = async (user) => {
    if (!window.confirm(`Отозвать все активные сессии пользователя "${user.username}"? Он будет разлогинен на всех устройствах.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const response = await fetch(`${apiUrl}/api/admin/users/${user._id}/revoke-sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Не удалось отозвать сессии' }));
        throw new Error(error.message || 'Не удалось отозвать сессии');
      }

      showSuccess('Сессии пользователя отозваны');
    } catch (err) {
      showError(err.message || 'Не удалось отозвать сессии');
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setEditUser({ role: user.role, password: '' });
    setShowEditModal(true);
  };

  const getRoleIcon = (role) => {
    return role === 'admin' ? <Shield size={16} color="#ef4444" /> : <User size={16} color="#3b82f6" />;
  };

  const getRoleColor = (role) => {
    return role === 'admin' ? '#ef4444' : '#3b82f6';
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--bg-primary)',
      }}>
        <div>Загрузка пользователей...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '28px' }}>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)' }}>
          <Users size={24} color="white" />
        </div>
        <div className="page-header-text">
          <h1>Управление пользователями</h1>
          <p>Пользователи и права в системе</p>
        </div>
      </div>

      {/* Create User Button */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 20px',
            border: 'none',
            borderRadius: '8px',
            backgroundColor: '#10b981',
            color: 'white',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#059669';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#10b981';
          }}
        >
          <Plus size={16} />
          Создать пользователя
        </button>
      </div>

      {/* Users Table */}
      <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
        <table className="data-table" style={{ 
          width: '100%',
          borderCollapse: 'collapse',
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: 'var(--shadow)',
          border: '1px solid var(--border-color)'
        }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--bg-tertiary)' }}>
              <th style={{ 
                padding: '16px 20px', 
                textAlign: 'left', 
                fontWeight: '600', 
                color: 'var(--text-primary)',
                borderBottom: '1px solid var(--border-color)',
                width: '30%'
              }}>Пользователь</th>
              <th style={{ 
                padding: '16px 20px', 
                textAlign: 'left', 
                fontWeight: '600', 
                color: 'var(--text-primary)',
                borderBottom: '1px solid var(--border-color)',
                width: '20%'
              }}>Роль</th>
              <th style={{ 
                padding: '16px 20px', 
                textAlign: 'left', 
                fontWeight: '600', 
                color: 'var(--text-primary)',
                borderBottom: '1px solid var(--border-color)',
                width: '25%'
              }}>Создан</th>
              <th style={{ 
                padding: '16px 20px', 
                textAlign: 'left', 
                fontWeight: '600', 
                color: 'var(--text-primary)',
                borderBottom: '1px solid var(--border-color)',
                width: '25%'
              }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user._id} style={{
                borderBottom: '1px solid var(--border-color)',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}>
                <td style={{ padding: '16px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <User size={16} color="var(--text-secondary)" />
                    <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>
                      {user.username}
                    </span>
                  </div>
                </td>
                <td style={{ padding: '16px 20px' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    color: getRoleColor(user.role)
                  }}>
                    {getRoleIcon(user.role)}
                    <span style={{ textTransform: 'capitalize', fontSize: '14px' }}>
                      {user.role}
                    </span>
                  </div>
                </td>
                <td style={{ padding: '16px 20px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                  {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Н/Д'}
                </td>
                <td style={{ padding: '16px 20px' }}>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => openEditModal(user)}
                      style={{
                        padding: '6px 10px',
                        border: '1px solid #3b82f6',
                        borderRadius: '6px',
                        backgroundColor: 'transparent',
                        color: '#3b82f6',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#3b82f6';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#3b82f6';
                      }}
                      title="Изменить пользователя"
                    >
                      <Edit size={12} />
                      Изменить
                    </button>
                    <button
                      onClick={() => handleRevokeSessions(user)}
                      style={{
                        padding: '6px 10px',
                        border: '1px solid #f59e0b',
                        borderRadius: '6px',
                        backgroundColor: 'transparent',
                        color: '#f59e0b',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f59e0b';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#f59e0b';
                      }}
                      title="Отозвать все сессии пользователя"
                    >
                      <Key size={12} />
                      Сбросить сессии
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user)}
                      style={{
                        padding: '6px 10px',
                        border: '1px solid #ef4444',
                        borderRadius: '6px',
                        backgroundColor: 'transparent',
                        color: '#ef4444',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#ef4444';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#ef4444';
                      }}
                      title="Удалить пользователя"
                    >
                      <Trash2 size={12} />
                      Удалить
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && (
          <div style={{
            padding: '48px',
            textAlign: 'center',
            color: 'var(--text-secondary)',
          }}>
            Пользователи не найдены
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                Создать пользователя
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewUser({ username: '', password: '' });
                }}
                style={{
                  padding: '4px 8px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                <X size={16} />
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                marginBottom: '4px'
              }}>
                Имя пользователя
              </label>
              <input
                type="text"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                placeholder="Введите имя пользователя"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                marginBottom: '4px'
              }}>
                Пароль
              </label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Введите пароль"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewUser({ username: '', password: '' });
                }}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Отмена
              </button>
              <button
                onClick={handleCreateUser}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Создать пользователя
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                Изменение пользователя: {editingUser.username}
              </h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingUser(null);
                  setEditUser({ role: '', password: '' });
                }}
                style={{
                  padding: '4px 8px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                <X size={16} />
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                marginBottom: '4px'
              }}>
                Роль
              </label>
              <select
                value={editUser.role}
                onChange={(e) => setEditUser({ ...editUser, role: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)'
                }}
              >
                <option value="">Выберите роль</option>
                <option value="user">Пользователь</option>
                <option value="admin">Администратор</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--text-primary)',
                marginBottom: '4px'
              }}>
                Новый пароль (оставьте пустым, чтобы не менять)
              </label>
              <input
                type="password"
                value={editUser.password}
                onChange={(e) => setEditUser({ ...editUser, password: e.target.value })}
                placeholder="Введите новый пароль"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingUser(null);
                  setEditUser({ role: '', password: '' });
                }}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Отмена
              </button>
              <button
                onClick={handleEditUser}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Обновить пользователя
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
